<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    @click.self="$emit('close')"
  >
    <div
      class="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col"
    >
      <!-- Header -->
      <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h3 class="text-lg font-semibold text-gray-900">
          {{ $t('translation.import.modal.title') }}
        </h3>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
          <XIcon class="w-5 h-5" />
        </button>
      </div>

      <!-- Contenu -->
      <div class="flex-1 overflow-y-auto p-6">
        <!-- Sélection fichier -->
        <div v-if="!importedData" class="mb-6">
          <p class="text-sm text-gray-500 mb-4">
            Sélectionnez un fichier JSON de traduction (ex: fr.json, en.json, it.json)
          </p>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t('translation.import.file.label') }}
          </label>
          <input
            type="file"
            accept=".json"
            @change="$emit('fileImport', $event)"
            class="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
          />
        </div>

        <!-- Sélection langue (toujours visible après import) -->
        <div v-if="importedData" class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t('translation.import.language.label') }}
          </label>
          <select
            :value="importLanguageCode"
            @change="handleLanguageChange"
            class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">{{ $t('translation.import.language.select') }}</option>
            <option v-for="lang in availableLanguages" :key="lang.code" :value="lang.code">
              {{ lang.flag }} {{ lang.name }}
            </option>
          </select>
          <p v-if="!importLanguageCode" class="mt-2 text-sm text-gray-500">
            Sélectionnez la langue du fichier pour comparer avec les traductions existantes.
          </p>
        </div>

        <!-- Résultats comparaison -->
        <div v-if="importedData && comparisonResults" class="space-y-4">
          <!-- Statistiques -->
          <div class="grid grid-cols-3 gap-4 mb-6">
            <div class="bg-green-50 border border-green-200 rounded-lg p-4">
              <div class="text-sm font-medium text-green-800">
                {{ $t('translation.import.stats.identical') }}
              </div>
              <div class="text-2xl font-bold text-green-600">
                {{ comparisonResults.identical.length }}
              </div>
            </div>
            <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div class="text-sm font-medium text-yellow-800">
                {{ $t('translation.import.stats.different') }}
              </div>
              <div class="text-2xl font-bold text-yellow-600">
                {{ comparisonResults.different.length }}
              </div>
            </div>
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div class="text-sm font-medium text-blue-800">
                {{ $t('translation.import.stats.new') }}
              </div>
              <div class="text-2xl font-bold text-blue-600">
                {{ comparisonResults.new.length }}
              </div>
            </div>
          </div>

          <!-- Liste des traductions différentes -->
          <div v-if="comparisonResults.different.length > 0" class="mb-6">
            <div class="flex items-center justify-between mb-3">
              <h4 class="text-sm font-medium text-gray-900">
                {{ $t('translation.import.different.title') }}
                ({{
                  $t('translation.import.different.selected', {
                    selected: selectedDifferentCount,
                    total: comparisonResults.different.length,
                  })
                }})
              </h4>
              <div class="flex space-x-2">
                <button
                  @click="$emit('selectAllDifferent')"
                  class="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  {{ $t('translation.import.different.selectAll') }}
                </button>
                <button
                  @click="$emit('deselectAllDifferent')"
                  class="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  {{ $t('translation.import.different.deselectAll') }}
                </button>
              </div>
            </div>
            <div class="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
              <div
                v-for="item in comparisonResults.different"
                :key="item.keyPath"
                class="p-3 border-b border-gray-100 hover:bg-gray-50"
              >
                <div class="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    :checked="selectedForImport.has(item.keyPath)"
                    @change="$emit('toggleSelection', item.keyPath)"
                    class="mt-1"
                  />
                  <div class="flex-1">
                    <div class="text-sm font-medium text-gray-900">{{ item.keyPath }}</div>
                    <div class="mt-1 text-xs text-gray-600">
                      <div class="mb-1">
                        <span class="font-medium">{{
                          $t('translation.import.different.current')
                        }}</span>
                        <span class="ml-1">{{
                          item.currentValue || $t('translation.import.different.empty')
                        }}</span>
                      </div>
                      <div>
                        <span class="font-medium">{{
                          $t('translation.import.different.new')
                        }}</span>
                        <span class="ml-1 text-indigo-600">{{ item.newValue }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Liste des nouvelles traductions -->
          <div v-if="comparisonResults.new.length > 0">
            <div class="flex items-center justify-between mb-3">
              <h4 class="text-sm font-medium text-gray-900">
                {{ $t('translation.import.new.title') }}
                ({{
                  $t('translation.import.new.selected', {
                    selected: selectedNewCount,
                    total: comparisonResults.new.length,
                  })
                }})
              </h4>
              <div class="flex space-x-2">
                <button
                  @click="$emit('selectAllNew')"
                  class="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  {{ $t('translation.import.new.selectAll') }}
                </button>
                <button
                  @click="$emit('deselectAllNew')"
                  class="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  {{ $t('translation.import.new.deselectAll') }}
                </button>
              </div>
            </div>
            <div class="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
              <div
                v-for="item in comparisonResults.new"
                :key="item.keyPath"
                class="p-3 border-b border-gray-100 hover:bg-gray-50"
              >
                <div class="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    :checked="selectedForImport.has(item.keyPath)"
                    @change="$emit('toggleSelection', item.keyPath)"
                    class="mt-1"
                  />
                  <div class="flex-1">
                    <div class="text-sm font-medium text-gray-900">{{ item.keyPath }}</div>
                    <div class="mt-1 text-xs text-gray-600">
                      <span class="font-medium">{{ $t('translation.import.new.value') }}</span>
                      <span class="ml-1 text-indigo-600">{{ item.value }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
        <button
          @click="$emit('close')"
          class="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
        >
          {{ $t('translation.import.button.cancel') }}
        </button>
        <button
          v-if="importedData && comparisonResults"
          @click="$emit('import')"
          :disabled="importLoading || selectedForImport.size === 0 || !importLanguageCode"
          class="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <svg v-if="importLoading" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span
            >{{
              importLoading
                ? $t('translation.import.button.importing')
                : $t('translation.import.button.import')
            }}
            ({{ selectedForImport.size }})</span
          >
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { XIcon } from 'lucide-vue-next'

interface Language {
  code: string
  name: string
  flag: string
}

interface ComparisonResult {
  identical: Array<{ keyPath: string; value: string }>
  different: Array<{ keyPath: string; currentValue: string; newValue: string }>
  new: Array<{ keyPath: string; value: string }>
}

interface Props {
  show: boolean
  importedData: Record<string, string> | null
  importLanguageCode: string
  comparisonResults: ComparisonResult | null
  selectedForImport: Set<string>
  selectedDifferentCount: number
  selectedNewCount: number
  importLoading: boolean
  availableLanguages: Language[]
}

defineProps<Props>()

const emit = defineEmits<{
  close: []
  fileImport: [event: Event]
  languageChange: [value: string]
  toggleSelection: [keyPath: string]
  selectAllDifferent: []
  deselectAllDifferent: []
  selectAllNew: []
  deselectAllNew: []
  import: []
}>()

function handleLanguageChange(event: Event) {
  const target = event.target as HTMLSelectElement
  emit('languageChange', target.value)
}
</script>
